import { useState, useRef, useLayoutEffect, useCallback, RefObject } from 'react';

const SCROLL_OFFSET = 150; // offset to scroll to the next section
const TAB_HEIGHT = 48; // height of the tabs
const TOP_THRESHOLD = 100; // threshold to scroll to the top
const BOTTOM_THRESHOLD = 10; // threshold to scroll to the bottom
const SCROLL_TIMEOUT = 750; // timeout to scroll to the next section
const INITIAL_TAB_INDEX = 0;

type ScrollTabSyncConfig = Readonly<{
  scrollContainerRef: RefObject<HTMLElement>;
  sectionIds: string[];

  // if true, the top index will be -1
  useNegativeIndexForTop?: boolean;
}>;

export const useScrollTabSync = ({
  scrollContainerRef,
  sectionIds,
  useNegativeIndexForTop = false,
}: ScrollTabSyncConfig) => {
  const [activeTabIndex, setActiveTabIndex] = useState(INITIAL_TAB_INDEX);
  const isAutoScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useLayoutEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;
      if (isAutoScrollingRef.current) return;

      const scrollPosition = scrollContainerRef.current.scrollTop + SCROLL_OFFSET;
      const isAtBottom =
        scrollContainerRef.current.scrollTop + scrollContainerRef.current.clientHeight >=
        scrollContainerRef.current.scrollHeight - BOTTOM_THRESHOLD;
      const isAtTop = scrollContainerRef.current.scrollTop < TOP_THRESHOLD;

      // If we're near the top of the page, set to first tab (or -1 if needed)
      if (isAtTop) {
        setActiveTabIndex(useNegativeIndexForTop ? -1 : 0);

        return;
      }

      // If we're at the bottom of the page, set to last section
      if (isAtBottom && sectionIds.length > 0) {
        setActiveTabIndex(sectionIds.length - 1);

        return;
      }

      const newIndex = sectionIds.findIndex((sectionId) => {
        const element = document.getElementById(sectionId);

        if (element && scrollContainerRef.current) {
          const sectionTop = element.offsetTop - scrollContainerRef.current.offsetTop;

          return scrollPosition >= sectionTop && scrollPosition < sectionTop + element.offsetHeight;
        }

        return false;
      });

      if (newIndex !== -1) {
        setActiveTabIndex(newIndex);
      }
    };

    const container = scrollContainerRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [sectionIds, scrollContainerRef, useNegativeIndexForTop]);

  const handleTabChange = useCallback(
    (newIndex: number) => {
      setActiveTabIndex(newIndex);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      isAutoScrollingRef.current = true;

      if (newIndex === -1) {
        scrollContainerRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        const sectionId = sectionIds[newIndex];
        const element = sectionId ? document.getElementById(sectionId) : null;

        if (element && scrollContainerRef.current) {
          const sectionTop = element.offsetTop - scrollContainerRef.current.offsetTop;

          scrollContainerRef.current.scrollTo({
            top: sectionTop - TAB_HEIGHT,
            behavior: 'smooth',
          });
        }
      }

      scrollTimeoutRef.current = setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, SCROLL_TIMEOUT);
    },
    [sectionIds, scrollContainerRef],
  );

  return {
    activeTabIndex,
    handleTabChange,
  };
};
